import React, { useState, useEffect } from "react";
import Header from '../../component/Header';
import Sidebar from '../../component/SideBar';
import { Button, Modal, Box, TextField, Checkbox, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Apiconnection from "../../utils/Apiconnection";
import TableLoader from "../../utils/TableLoader";
import Loader from "../../utils/Loader";
import { toast } from "react-toastify";
import 'bootstrap/dist/css/bootstrap.min.css';
import Pagination from 'react-bootstrap/Pagination';

const Payments = () => {
    const [tableloading, settableloading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openSidebarToggle, setOpenSidebarToggle] = useState(false);
    
    const [paymentList, setPaymentList] = useState([]);
    
    // Form States
    const [openModal, setOpenModal] = useState(false);
    const [methodName, setMethodName] = useState("");
    const [isQrRequired, setIsQrRequired] = useState(false);
    const [qrCode, setQrCode] = useState(null);

    // Edit States
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editedData, setEditedData] = useState(null);
    const [editMethodName, setEditMethodName] = useState("");
    const [editIsQrRequired, setEditIsQrRequired] = useState(false);
    const [editQrCode, setEditQrCode] = useState(null);
    const [existingQrCode, setExistingQrCode] = useState("");

    // Delete States
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Preview Modal State
    const [openPreviewModal, setOpenPreviewModal] = useState(false);
    const [previewImgUrl, setPreviewImgUrl] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle);
    };

    const handleOpenModal = () => {
        setMethodName("");
        setIsQrRequired(false);
        setQrCode(null);
        setOpenModal(true);
    };
    
    const handleCloseModal = () => setOpenModal(false);
    const handleCloseEditModal = () => setOpenEditModal(false);

    // Fetch Payment Methods
    const getPaymentList = async () => {
        settableloading(true);
        try {
            // Using POST for listing based on scratch API tests
            const response = await Apiconnection.post('payment-method-list');
            if (response?.data?.status) {
                const list = response.data.data?.paymentMethodList || response.data.data || [];
                setPaymentList(list);
            } else {
                toast.error(response?.data?.message || 'Failed to fetch payment methods.');
            }
        } catch (error) {
            console.error('Error fetching payment list:', error);
            toast.error('Failed to fetch payment list.');
        } finally {
            settableloading(false);
        }
    };

    useEffect(() => {
        getPaymentList();
    }, []);

    // Helper for QR Code URL
    const getQrCodeUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://v2-api.primeparkusa.com';
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        
        let cleanPath = path;
        if (!cleanPath.startsWith('uploads/payment-methods/') && !cleanPath.startsWith('/uploads/payment-methods/')) {
            if (cleanPath.startsWith('/')) {
                cleanPath = `uploads/payment-methods${cleanPath}`;
            } else {
                cleanPath = `uploads/payment-methods/${cleanPath}`;
            }
        }
        
        if (cleanPath.startsWith('/')) {
            cleanPath = cleanPath.slice(1);
        }
        
        return `${cleanBaseUrl}/${cleanPath}`;
    };

    // Add Payment Method Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isQrRequired && !qrCode) {
            toast.error('Please upload a QR Code image.');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('methodName', methodName);
            formData.append('isQrRequired', isQrRequired ? 'true' : 'false');
            if (qrCode) {
                formData.append('qrCode', qrCode);
            }

            const response = await Apiconnection.post('payment-method-add', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.status) {
                toast.success('Payment method added successfully!');
                handleCloseModal();
                getPaymentList();
            } else {
                toast.error(response?.data?.message || 'Failed to add payment method.');
            }
        } catch (error) {
            console.error('Error adding payment method:', error);
            toast.error(error.response?.data?.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    // Click Edit
    const handleEditClick = (row) => {
        setEditedData(row);
        const paymentMethodId = row.paymentMethodId || row.id;
        fetchPaymentMethodDetail(paymentMethodId);
    };

    // Fetch details by ID
    const fetchPaymentMethodDetail = async (id) => {
        setLoading(true);
        try {
            const response = await Apiconnection.post('payment-method-detail', { paymentMethodId: id });
            if (response?.data?.status) {
                const detailData = response.data.data;
                setEditMethodName(detailData.methodName || "");
                setEditIsQrRequired(detailData.isQrRequired === true || detailData.isQrRequired === 1 || detailData.isQrRequired === 'true');
                setExistingQrCode(detailData.qrCode || "");
                setEditQrCode(null);
                setOpenEditModal(true);
            } else {
                toast.error(response?.data?.message || 'Failed to fetch details.');
            }
        } catch (error) {
            console.error('Error fetching details:', error);
            toast.error('Failed to fetch payment method details.');
        } finally {
            setLoading(false);
        }
    };

    // Edit Payment Method Submit
    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (editIsQrRequired && !existingQrCode && !editQrCode) {
            toast.error('Please upload a QR Code image.');
            return;
        }

        setLoading(true);
        try {
            const paymentMethodId = editedData.paymentMethodId || editedData.id;
            const formData = new FormData();
            formData.append('paymentMethodId', paymentMethodId);
            formData.append('methodName', editMethodName);
            formData.append('isQrRequired', editIsQrRequired ? 'true' : 'false');
            if (editQrCode) {
                formData.append('qrCode', editQrCode);
            }

            const response = await Apiconnection.post('payment-method-update', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.status) {
                toast.success('Payment method updated successfully!');
                handleCloseEditModal();
                getPaymentList();
            } else {
                toast.error(response?.data?.message || 'Failed to update payment method.');
            }
        } catch (error) {
            console.error('Error updating payment method:', error);
            toast.error(error.response?.data?.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    // Click Delete
    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setOpenConfirmDelete(true);
    };

    // Confirm Delete
    const handleConfirmDelete = async () => {
        setLoading(true);
        try {
            const paymentMethodId = itemToDelete.paymentMethodId || itemToDelete.id;
            const response = await Apiconnection.post('payment-method-delete', { paymentMethodId });
            if (response?.data?.status) {
                toast.success('Payment method deleted successfully!');
                getPaymentList();
            } else {
                toast.error(response?.data?.message || 'Failed to delete payment method.');
            }
        } catch (error) {
            console.error('Error deleting payment method:', error);
            toast.error('Failed to delete payment method.');
        } finally {
            setLoading(false);
            setOpenConfirmDelete(false);
        }
    };

    // Preview QR image
    const handlePreviewQr = (path) => {
        setPreviewImgUrl(getQrCodeUrl(path));
        setOpenPreviewModal(true);
    };

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = paymentList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(paymentList.length / itemsPerPage);
    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <div className='grid-container'>
            <Header OpenSidebar={OpenSidebar} />
            {loading && <Loader />}
            <Sidebar openSidebarToggle={openSidebarToggle} OpenSidebar={OpenSidebar}/>
            
            <div className='user-list'>
                <div className='top-section'>
                    <h3>Payment Methods</h3>
                    <Button variant="contained" className="action-btn" onClick={handleOpenModal}>
                        <AddIcon />
                        Add Payment Method
                    </Button>
                </div>

                <div className='custom-table table-responsive user-table-wrap'>
                    {!tableloading ?
                        <table className='table user-data-table'>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Method Name</th>
                                    <th>QR Required</th>
                                    <th>QR Code</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems && currentItems.length > 0 ?
                                    currentItems.map((list, index) => {
                                        const qrRequired = list.isQrRequired === true || list.isQrRequired === 1 || list.isQrRequired === 'true';
                                        return (
                                            <tr key={list.paymentMethodId || list.id || index}>
                                                <td>{list.paymentMethodId || list.id}</td>
                                                <td className="user-name-cell">
                                                    <div className="user-primary-text">{list.methodName}</div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${qrRequired ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                        {qrRequired ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {qrRequired && list.qrCode ? (
                                                        <img 
                                                            src={getQrCodeUrl(list.qrCode)} 
                                                            alt="QR Code" 
                                                            style={{ width: '100px', height: '100px', objectFit: 'contain', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }} 
                                                            onClick={() => handlePreviewQr(list.qrCode)}
                                                        />
                                                    ) : (
                                                        '--'
                                                    )}
                                                </td>
                                                <td className="user-action-cell">
                                                    <div className="flx-box user-action-group">
                                                        <button className="btn btn-sm btn-outline-warning me-2 table-icon-btn table-edit-btn" onClick={() => handleEditClick(list)}>
                                                            <i className="fa-solid fa-pen-to-square"></i>
                                                        </button>
                                                        <button className='btn btn-sm btn-outline-danger table-icon-btn table-delete-btn' onClick={() => handleDeleteClick(list)}>
                                                            <i className="fa-solid fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })
                                    :
                                    <tr>
                                        <td colSpan="5" className='text-center empty-table-cell'>No data record</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                        :
                        <TableLoader />
                    }
                </div>
                {totalPages > 1 && (
                    <Pagination className="justify-content-end">
                        {paginationItems}
                    </Pagination>
                )}

                {/* Add Modal */}
                <Modal
                    open={openModal}
                    onClose={handleCloseModal}
                    aria-labelledby="add-payment-modal"
                    aria-describedby="add-payment-modal-description"
                >
                    <div className="right-open-modal">
                        <h2 id="modal-title">Add Payment Method</h2>
                        <form onSubmit={handleSubmit}>
                            <TextField
                                id="methodName"
                                label="Method Name"
                                className="mt-3"
                                value={methodName}
                                onChange={(e) => setMethodName(e.target.value)}
                                name="methodName"
                                required
                                fullWidth
                            />
                            
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isQrRequired}
                                        onChange={(e) => setIsQrRequired(e.target.checked)}
                                        name="isQrRequired"
                                        color="primary"
                                    />
                                }
                                label="QR Code Required"
                                style={{ marginTop: '15px', marginBottom: '15px' }}
                            />

                            {isQrRequired && (
                                <div className="mb-4">
                                    <label htmlFor="qrCode" className="form-label d-block">QR Code Image</label>
                                    <input
                                        id="qrCode"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setQrCode(e.target.files[0])}
                                        name="qrCode"
                                        className="form-control"
                                        required={isQrRequired}
                                    />
                                </div>
                            )}

                            <Button type="submit" variant="contained" color="primary" fullWidth className="mt-3">
                                Submit
                            </Button>
                        </form>
                    </div>
                </Modal>

                {/* Edit Modal */}
                <Modal
                    open={openEditModal}
                    onClose={handleCloseEditModal}
                    aria-labelledby="edit-payment-modal"
                    aria-describedby="edit-payment-modal-description"
                >
                    <div className="right-open-modal">
                        <h2 className="mb-3" id="modal-title">Edit Payment Method</h2>
                        <form onSubmit={handleEditSubmit}>
                            <TextField
                                id="editMethodName"
                                label="Method Name"
                                className="mt-3"
                                value={editMethodName}
                                onChange={(e) => setEditMethodName(e.target.value)}
                                name="editMethodName"
                                required
                                fullWidth
                            />
                            
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editIsQrRequired}
                                        onChange={(e) => setEditIsQrRequired(e.target.checked)}
                                        name="editIsQrRequired"
                                        color="primary"
                                    />
                                }
                                label="QR Code Required"
                                style={{ marginTop: '15px', marginBottom: '15px' }}
                            />

                            {editIsQrRequired && (
                                <div className="mb-4">
                                    {existingQrCode && (
                                        <div className="mb-2">
                                            <p className="mb-1 text-muted text-small">Existing QR Code:</p>
                                            <div className="mb-2" style={{ wordBreak: 'break-all', fontSize: '13px' }}>
                                            </div>
                                            <img 
                                                src={getQrCodeUrl(existingQrCode)} 
                                                alt="Existing QR" 
                                                style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain', border: '1px solid #ccc', borderRadius: '4px', padding: '4px' }} 
                                            />
                                        </div>
                                    )}
                                    <label htmlFor="editQrCode" className="form-label d-block">Upload New QR Code (Optional)</label>
                                    <input
                                        id="editQrCode"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setEditQrCode(e.target.files[0])}
                                        name="editQrCode"
                                        className="form-control"
                                    />
                                </div>
                            )}

                            <Button type="submit" variant="contained" color="primary" fullWidth className="mt-3">
                                Save Changes
                            </Button>
                        </form>
                    </div>
                </Modal>

                {/* Confirm Delete Modal */}
                <Modal
                    open={openConfirmDelete}
                    onClose={() => setOpenConfirmDelete(false)}
                    aria-labelledby="confirm-delete-modal"
                    aria-describedby="confirm-delete-modal-description"
                >
                    <Box component="form"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            fontSize: 15,
                            height: 200,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: '8px'
                        }} >
                        <h2 id="modal-title">Confirm Delete</h2>
                        <div className="mdl-inner">
                            <p className="mb-4 mt-3" style={{fontWeight: '600'}}>Are you sure you want to delete this payment method?</p>
                            <div className="flx-box d-flex justify-content-end">
                                <Button variant="contained" color="secondary" onClick={() => setOpenConfirmDelete(false)}>Cancel</Button>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                <Button variant="contained" color="error" onClick={handleConfirmDelete}>Yes, Delete</Button>
                            </div>
                        </div>
                    </Box>
                </Modal>

                {/* QR Code Preview Modal */}
                <Modal
                    open={openPreviewModal}
                    onClose={() => setOpenPreviewModal(false)}
                    aria-labelledby="qr-preview-modal"
                    aria-describedby="qr-preview-modal-description"
                >
                    <Box 
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 3,
                            borderRadius: '8px',
                            textAlign: 'center',
                            maxWidth: '90vw',
                            maxHeight: '90vh'
                        }}
                    >
                        <h4 className="mb-3">QR Code Preview</h4>
                        <img 
                            src={previewImgUrl} 
                            alt="QR Code Preview" 
                            style={{ maxWidth: '350px', maxHeight: '350px', width: '100%', height: 'auto', objectFit: 'contain', border: '1px solid #eee', padding: '5px', borderRadius: '4px' }} 
                        />
                        <div className="mt-3">
                            <Button variant="contained" color="primary" onClick={() => setOpenPreviewModal(false)}>Close</Button>
                        </div>
                    </Box>
                </Modal>
            </div>
        </div>
    );
};

export default Payments;